/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState, Fragment } from "react";
import Layout from "../components/Layout";
import { fmtDate } from "../lib/format";
import api from "../lib/api";
import { toast } from "sonner";
import { CloudArrowUp, ArrowsClockwise, CaretDown, CaretRight, ShieldCheck } from "@phosphor-icons/react";

const STATUS_BADGE = {
  ready: "shipped",
  customer_unmapped: "alert",
  sku_unmapped: "warning",
};
const STATUS_LABEL = {
  ready: "Ready to push",
  customer_unmapped: "Customer unmapped",
  sku_unmapped: "SKU unmapped",
};

function Tile({ label, value, tone, testid }) {
  return (
    <div className="jde-card" style={{ padding: "12px 16px", minWidth: 120 }} data-testid={testid}>
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: tone }}>{value}</div>
    </div>
  );
}

export default function PortalPush() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [pushing, setPushing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/portal/push-readiness")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load readiness report"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pushPo = (row) => {
    const skipNote = row.skipped_line_count
      ? `\n\n${row.skipped_line_count} unmapped line(s) will be SKIPPED.` : "";
    const ok = window.confirm(
      `LIVE WRITE to the MS SQL portal.\n\n` +
      `PO ${row.custpo} → Pcode ${row.pcode}\n` +
      `${row.mapped_line_count} line(s), amount ${row.amount}${skipNote}\n\n` +
      `This inserts a real PO via Pro_po_Insert / Pro_poi_Insert. Continue?`);
    if (!ok) return;
    setPushing(row.id);
    api.post("/portal/push", { po_id: row.id, confirm: true })
      .then((r) => {
        const d = r.data;
        if (d.status === "pushed") {
          toast.success(`Pushed → PoNo ${d.portal_pono} (${d.lines_inserted} lines${d.lines_skipped ? `, ${d.lines_skipped} skipped` : ""})`);
        } else if (d.status === "already_in_portal" || d.status === "already_pushed") {
          toast.info(`Already in portal → PoNo ${d.portal_pono}`);
        } else {
          toast.warning(`Not pushed: ${d.status}`);
        }
        load();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Push failed"))
      .finally(() => setPushing(null));
  };

  const rows = (data?.rows || []).filter((r) => filter === "all" || r.po_status === filter);
  const s = data?.summary;

  return (
    <Layout>
      <div className="space-y-6" data-testid="portal-push-page">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 20, fontWeight: 800 }}>
              <CloudArrowUp size={22} /> Portal Push — MS SQL
            </h1>
            <p className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              Resolves each intake PO (customer → <code>Pcode</code>, each SKU → <code>item.icode</code>), then pushes ready POs to the portal via stored procs. Unmapped lines are skipped.
            </p>
          </div>
          <button className="jde-btn jde-btn-primary" onClick={load} disabled={loading} data-testid="refresh-readiness-btn">
            <ArrowsClockwise size={15} /> {loading ? "Checking…" : "Re-check"}
          </button>
        </div>

        <div className="jde-card" style={{ padding: "10px 14px", background: "#ECFDF5", borderColor: "#A7F3D0", display: "flex", gap: 8, alignItems: "center" }}>
          <ShieldCheck size={18} color="#047857" />
          <span style={{ fontSize: 12.5, color: "#065F46" }}>
            <strong>Read-only.</strong> This screen only runs <code>SELECT</code> queries against the portal — no PO is created or modified. Nothing is written until you explicitly run a push.
          </span>
        </div>

        {s && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Tile label="Intake POs" value={s.total_intake_pos} tone="#111827" testid="tile-total" />
            <Tile label="Ready" value={s.ready} tone="#047857" testid="tile-ready" />
            <Tile label="Customer unmapped" value={s.customer_unmapped} tone="#B91C1C" testid="tile-cust" />
            <Tile label="SKU unmapped" value={s.sku_unmapped} tone="#9A3412" testid="tile-sku" />
            <Tile label="Party master" value={s.party_master_count} tone="#6B7280" testid="tile-party" />
            <Tile label="Item master" value={s.item_master_count} tone="#6B7280" testid="tile-item" />
          </div>
        )}

        <div style={{ display: "flex", gap: 6 }}>
          {["all", "ready", "customer_unmapped", "sku_unmapped"].map((f) => (
            <button key={f} className={`jde-btn jde-btn-sm ${filter === f ? "jde-btn-primary" : ""}`}
                    onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
              {f === "all" ? "All" : STATUS_LABEL[f]}
            </button>
          ))}
        </div>

        <div className="jde-card" style={{ padding: 0, overflow: "auto" }}>
          <table className="jde-table" data-testid="readiness-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>PO #</th>
                <th>Customer</th>
                <th>Pcode</th>
                <th>Status</th>
                <th className="num">Lines</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr data-testid={`po-row-${r.po_number}`} style={{ cursor: "pointer" }}
                      onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}>
                    <td>{expanded[r.id] ? <CaretDown size={14} /> : <CaretRight size={14} />}</td>
                    <td><strong>{r.po_number || "—"}</strong>{r.is_change_order && <span className="jde-badge warning" style={{ marginLeft: 6 }}>CHANGE</span>}</td>
                    <td>{r.customer_name}</td>
                    <td>
                      {r.pcode_status === "ok" && <span className="text-mono">{r.pcode}</span>}
                      {r.pcode_status === "ambiguous" && <span className="jde-badge alert">ambiguous ({r.pcode_candidates.length})</span>}
                      {r.pcode_status === "unmapped" && <span className="jde-badge alert">not found</span>}
                    </td>
                    <td><span className={`jde-badge ${STATUS_BADGE[r.po_status]}`}>{STATUS_LABEL[r.po_status]}</span>
                      {r.portal_pono && <span className="jde-badge shipped" style={{ marginLeft: 6 }} data-testid={`pushed-${r.po_number}`}>→ PoNo {r.portal_pono}</span>}
                    </td>
                    <td className="num">{r.line_count}</td>
                  </tr>
                  {expanded[r.id] && (
                    <tr key={r.id + "-d"}>
                      <td></td>
                      <td colSpan={5} style={{ background: "#F9FAFB" }}>
                        {r.pcode_status === "ambiguous" && (
                          <div style={{ fontSize: 12, marginBottom: 6, color: "#B91C1C" }}>
                            Matches multiple customers — pick the right <code>Pcode</code>:{" "}
                            {r.pcode_candidates.map((c) => `${c.pcode} (${c.name})`).join(", ")}
                          </div>
                        )}
                        <table className="jde-table" style={{ margin: 0 }}>
                          <thead>
                            <tr>
                              <th>SKU</th><th>Rev</th><th>Description</th><th className="num">Qty</th>
                              <th>Due</th><th>→ icode</th><th>Match</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.lines.map((li, i) => (
                              <tr key={i} data-testid={`line-${r.po_number}-${i}`}>
                                <td className="text-mono">{li.sku || "—"}</td>
                                <td>{li.rev || "—"}</td>
                                <td>{(li.description || "").slice(0, 40)}</td>
                                <td className="num">{li.quantity_ordered}</td>
                                <td>{fmtDate(li.due_date)}</td>
                                <td className="text-mono">{li.icode || "—"}</td>
                                <td>
                                  {li.icode_status === "ok" && <span className="jde-badge shipped">via {li.via}</span>}
                                  {li.icode_status === "ambiguous" && <span className="jde-badge warning">ambiguous</span>}
                                  {li.icode_status === "unmapped" && <span className="jde-badge alert">no item</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {r.po_status === "ready" && (
                          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            {r.portal_pono ? (
                              <span className="jde-badge shipped" data-testid={`push-done-${r.po_number}`}>
                                Pushed → portal PoNo {r.portal_pono}
                              </span>
                            ) : (
                              <>
                                <button className="jde-btn jde-btn-primary jde-btn-sm"
                                        disabled={pushing === r.id}
                                        onClick={(e) => { e.stopPropagation(); pushPo(r); }}
                                        data-testid={`push-btn-${r.po_number}`}>
                                  <CloudArrowUp size={14} /> {pushing === r.id ? "Pushing…" : "Push this PO to portal"}
                                </button>
                                <span className="muted" style={{ fontSize: 12 }}>
                                  Inserts <strong>{r.mapped_line_count}</strong> line(s) under <code>{r.pcode}</code> (CustPO <code>{r.custpo}</code>, amount {r.amount}).
                                  {r.skipped_line_count > 0 && (
                                    <span style={{ color: "#9A3412" }}> {r.skipped_line_count} unmapped line(s) skipped.</span>
                                  )}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 24 }}>No POs for this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
